// La caja de prompt del modo Redes — ADR-2026-10-01-modo-redes.
//
// Es `PromptBoxLibre` (la misma caja de Prompt libre y del Creador) metida en un marco
// propio, con el prompt a todo el ancho y el Generar plano al final de la fila de
// opciones. A la izquierda del marco, el interruptor Imagen/Video en vertical. Producto
// y modelo entran por las TARJETAS de arriba de la caja (Añadir producto, Añadir modelo, Añadir referencia:
// el selector del Agente, cada una en su pestaña), que muestran lo elegido en miniatura. Debajo del prompt, las opciones como
// pastillas —Añadir producto, Añadir modelo, Añadir referencia, Configuración, Cantidad o Tiempo, Prompts,
// Recientes, Ayuda de prompt—; cada una abre su panel portaleado sobre el botón, como los
// menús del composer (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean).
//
// El marco: sin borde marcado y con una sombra larga por debajo, que es lo que se pidió
// ("menos bordes y más sombra"). El anillo azul sólo con el foco adentro.

import React, { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Check, Clapperboard, Image as ImageIcon, Loader2, Minus, Package, Plus, Settings2, Sparkles, Upload, User, Wand2, FlaskConical } from 'lucide-react';
import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { useUserProducts } from '../../productsContext';
import { useUserModels } from '../../modelsContext';
import { TarjetaDeReferencia } from './TarjetaDeReferencia';
import { usePosicionSobreElBoton } from '../../components/posicionSobreElBoton';
import { OPCIONES_RESOLUCION, type ResolucionImagen } from '../../../../lib/resolucionImagen';
import { AYUDA_BORRADOR, RESOLUCIONES_VIDEO, esBorrador } from '../../../../lib/resolucionVideo';
import { EtiquetaBorrador } from '../../../../components/EtiquetaBorrador';
import { videoGeneralRatePerSec, type VideoResolution } from '../../../../utils/credits';
import type { IdDeFuente } from '../../../../chat-atlas/ui/ModalDeFuentes';
import { PromptBoxLibre } from '../promptLibre/PromptBoxLibre';
import { MAX_CANTIDAD, VIDEO_DURATION_MIN, videoDuracionMax, type VideoQuality } from '../asistente/directMode';
import { TipoDeVideoAnclado } from '../creadorVideo/TipoDeVideoPill';
import { conCalidad } from '../creadorVideo/creadorVideo';
import { palabraDeModeloActual } from '../../../../lib/nombreDeModelo';
import { BibliotecaDePrompts } from './BibliotecaDePrompts';
import WakeSlider from '../../../../components/WakeSlider';
import StatusMark, { type EstadoDeMarca } from '../../../../components/StatusMark';
import { PROVEEDORES_DE_IMAGEN, etiquetaProveedorDeImagen, type ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';
import {
  DURACION_MEDIA_DE_MEJORA_MS,
  creditosDeRedes,
  formatoActivo,
  placeholderDeRedes,
  rotuloDeFormato,
  type AjustesDeRedes,
  type Medio,
} from './redes';

export interface CajaDeRedesProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  onGenerar: () => void;
  /** Qué falta para generar: apaga la flecha. */
  falta?: string;
  generando?: boolean;
  adjuntos: ChatAttachment[];
  onQuitarAdjunto: (id: string) => void;
  /** Abre el selector del Agente en esa pestaña (Producto → productos, Modelo → modelos, Añadir referencia → subidas). */
  onAbrirFuentes: (pestana: IdDeFuente) => void;
  ajustes: AjustesDeRedes;
  onAjustes: (fn: (a: AjustesDeRedes) => AjustesDeRedes) => void;
  /** Sin esto la pastilla "Ayuda de prompt" no se dibuja (el plan no tiene Agente). */
  onAyudaDePrompt?: () => void;
  /**
   * "Mejorar prompt": el escritor del medio activo (el del Creador en video, el de las
   * reglas del agente en imagen) escribe el prompt con lo que hay en la caja y lo reemplaza
   * (ADR-2026-10-04-mejorar-prompt-en-redes,
   * ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente).
   */
  onMejorarPrompt?: () => void;
  mejorando?: boolean;
  /** Cómo terminó la última mejora: el botón lo muestra un momento (tilde o cruz) y vuelve. */
  resultadoDeMejora?: 'listo' | 'fallo' | null;
  /** Hay una imagen pegada o soltada subiéndose: la tarjeta "Añadir referencia" gira. */
  subiendo?: boolean;
  /**
   * SÓLO PARA CUENTAS DEV (`dev_tester`): fuerza con qué motor de imagen se genera, pisando
   * lo que elegiría el escritor por la tarea (ADR-2026-10-05-forzar-el-modelo-en-redes).
   * Sin `onProveedorForzado` la pastilla no se dibuja, que es lo que ve cualquier cliente.
   */
  proveedorForzado?: ProveedorDeImagen | null;
  onProveedorForzado?: (p: ProveedorDeImagen | null) => void;
  className?: string;
}

/**
 * El alto de REPOSO de la caja (px): la fila de tarjetas, unas dos líneas de prompt y la
 * fila de opciones.
 * Lo comparten el marco (como mínimo) y el interruptor (fijo): con un prompt largo el
 * marco crece y el interruptor no.
 */
const ALTO_CAJA = 244;

/** La pastilla de la fila de opciones: `h-9`, redonda, gris claro — la de "Recientes". */
const PASTILLA =
  'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-100 px-3.5 text-[13.5px] font-medium text-[#02062BB3] transform transition-transform duration-300 hover:-translate-y-0.5';
const PASTILLA_ABIERTA = 'bg-[#E8EEFF] text-brand-primary hover:bg-[#E8EEFF]';

/**
 * Un panel que se abre HACIA ARRIBA desde una pastilla, portaleado al body. Cierra con
 * Escape y con un click afuera (el botón que lo abrió lo cierra por su cuenta).
 */
const PanelSobrePastilla: React.FC<{
  botonRef: RefObject<HTMLButtonElement | null>;
  abierto: boolean;
  onCerrar: () => void;
  ancho: number;
  children: ReactNode;
}> = ({ botonRef, abierto, onCerrar, ancho, children }) => {
  const pos = usePosicionSobreElBoton(botonRef, abierto, ancho);
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    const alClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || botonRef.current?.contains(t)) return;
      onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alClick);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alClick);
    };
  }, [abierto, onCerrar, botonRef]);
  if (!abierto || !pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      className="animate-mode-swap fixed z-[130] rounded-2xl border border-black/[0.06] bg-white p-1.5 shadow-[0_18px_50px_rgba(2,6,43,0.16)]"
      style={{ left: pos.left, bottom: pos.bottom, width: ancho }}
    >
      {children}
    </div>,
    document.body,
  );
};

/** Una fila de opción de los paneles: texto a la izquierda, tilde a la derecha. */
const Opcion: React.FC<{ activa: boolean; onClick: () => void; children: ReactNode; detalle?: ReactNode }> = ({ activa, onClick, children, detalle }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${activa ? 'bg-[#1644EB0F]' : 'hover:bg-neutral-50'}`}
  >
    <span className="min-w-0">
      <span className={`block text-[14px] ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{children}</span>
      {detalle && <span className="mt-0.5 block text-[12px] text-[#3C3C43]/55">{detalle}</span>}
    </span>
    {activa && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
  </button>
);

/** El interruptor Imagen/Video, en VERTICAL a la izquierda de la caja. */
const ToggleDeMedio: React.FC<{ value: Medio; onChange: (m: Medio) => void }> = ({ value, onChange }) => {
  const idx = value === 'video' ? 1 : 0;
  const opciones: { id: Medio; rotulo: string; Icono: typeof ImageIcon }[] = [
    { id: 'imagen', rotulo: 'Imagen', Icono: ImageIcon },
    { id: 'video', rotulo: 'Video', Icono: Clapperboard },
  ];
  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label="Qué querés crear"
      // Alto FIJO y alineado abajo: cuando el prompt crece, el marco de al lado se estira
      // y éste se queda en su lugar.
      style={{ height: ALTO_CAJA }}
      className="relative flex w-[112px] shrink-0 flex-col self-end rounded-[22px] bg-[#E9EBF1] p-1.5 ring-1 ring-black/[0.04]"
    >
      {/* El indicador de la opción elegida: una pastilla BLANCA que se desliza sobre el
          riel gris — el azul de la caja es del Generar, y dos bloques azules a la misma
          altura competían. */}
      <span
        aria-hidden="true"
        className="absolute bottom-1/2 left-1.5 right-1.5 top-1.5 rounded-2xl bg-white shadow-[0_2px_8px_rgba(2,6,43,0.08)] transition-transform duration-300 ease-out"
        style={{ transform: `translateY(${idx * 100}%)` }}
      />
      {opciones.map(({ id, rotulo, Icono }) => {
        const activa = id === value;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activa}
            onClick={() => onChange(id)}
            className={`relative z-10 flex flex-1 flex-col items-center justify-center gap-1.5 rounded-2xl text-[13px] font-semibold transition-colors duration-300 ${activa ? 'text-brand-black' : 'text-[#02062B80] hover:text-brand-black'}`}
          >
            <Icono className="h-5 w-5" strokeWidth={2} />
            {rotulo}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Un número que RUEDA al cambiar: el viejo se va hacia arriba y el nuevo entra desde abajo
 * (al bajar, al revés). Es el costo del Generar, que cambia con la cantidad, la calidad y
 * la duración. Las dos animaciones viven en `index.css` (`contador-sale` / `contador-entra`).
 */
const NumeroRodante: React.FC<{ valor: number }> = ({ valor }) => {
  const [estado, setEstado] = useState<{ actual: number; anterior: number | null; dir: 1 | -1 }>({ actual: valor, anterior: null, dir: 1 });
  useEffect(() => {
    if (valor === estado.actual) return;
    setEstado((e) => ({ actual: valor, anterior: e.actual, dir: valor > e.actual ? 1 : -1 }));
    const t = window.setTimeout(() => setEstado((e) => ({ ...e, anterior: null })), 320);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  const estilo = { '--dir': estado.dir } as React.CSSProperties;
  return (
    <span className="relative inline-block h-[1.25em] min-w-[1ch] overflow-hidden align-middle tabular-nums" style={estilo}>
      {estado.anterior !== null && (
        <span key={`sale-${estado.anterior}-${estado.actual}`} className="absolute inset-x-0 top-0 animate-contador-sale leading-[1.25em]">
          {estado.anterior}
        </span>
      )}
      <span key={`entra-${estado.actual}`} className={`block leading-[1.25em] ${estado.anterior !== null ? 'animate-contador-entra' : ''}`}>
        {estado.actual}
      </span>
    </span>
  );
};

// `TarjetaDeReferencia` vive en su archivo: la comparte la barra de Editar del visor
// (ADR-2026-10-06-editar-con-referencias-y-escritor).

export const CajaDeRedes: React.FC<CajaDeRedesProps> = ({
  prompt,
  onPromptChange,
  onGenerar,
  falta,
  generando = false,
  adjuntos,
  onQuitarAdjunto,
  onAbrirFuentes,
  ajustes,
  onAjustes,
  onAyudaDePrompt,
  onMejorarPrompt,
  mejorando = false,
  resultadoDeMejora = null,
  subiendo = false,
  proveedorForzado = null,
  onProveedorForzado,
  className = '',
}) => {
  const esVideo = ajustes.medio === 'video';
  const creditos = creditosDeRedes(ajustes);
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  // Producto, modelo e imágenes subidas se ven en las tarjetas de arriba; lo demás (poses,
  // fondos, creaciones del selector) sigue en los chips de la caja.
  const productos = adjuntos.filter((a) => a.kind === 'product');
  const modelos = adjuntos.filter((a) => a.kind === 'model');
  // Un video sumado como referencia va con las imágenes, en "Añadir referencia".
  const subidas = adjuntos.filter((a) => a.kind === 'image' || a.kind === 'video');
  const otros = adjuntos.filter((a) => a.kind !== 'product' && a.kind !== 'model' && a.kind !== 'image' && a.kind !== 'video');
  const [panel, setPanel] = useState<'configuracion' | 'tiempo' | 'modelo' | null>(null);
  const refModelo = useRef<HTMLButtonElement | null>(null);
  const [biblioteca, setBiblioteca] = useState(false);
  const refConfig = useRef<HTMLButtonElement | null>(null);
  const refTiempo = useRef<HTMLButtonElement | null>(null);
  const cerrar = () => setPanel(null);
  // La marca de estado de "Mejorar prompt" (ADR-2026-10-04-status-mark-en-mejorar-prompt):
  // al tocar, nace como anillo de rayitas y un cuadro después pasa a "en curso", así se ve
  // la fusión en el arco que gira; al terminar, el tilde (o la cruz) un momento.
  const [arcoArrancado, setArcoArrancado] = useState(false);
  useEffect(() => {
    if (!mejorando) {
      setArcoArrancado(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setArcoArrancado(true));
    return () => window.cancelAnimationFrame(id);
  }, [mejorando]);
  // Cuando la mejora termina bien, el texto nuevo de la caja hace un rebote sutil.
  const [pulsoDeTexto, setPulsoDeTexto] = useState(0);
  useEffect(() => {
    if (resultadoDeMejora === 'listo') setPulsoDeTexto((p) => p + 1);
  }, [resultadoDeMejora]);
  const marcaDeMejora: EstadoDeMarca | null = mejorando
    ? arcoArrancado
      ? 'running'
      : 'pending'
    : resultadoDeMejora === 'listo'
      ? 'done'
      : resultadoDeMejora === 'fallo'
        ? 'failed'
        : null;
  const alternar = (p: NonNullable<typeof panel>) => setPanel((v) => (v === p ? null : p));

  const setImagen = (fn: (i: AjustesDeRedes['imagen']) => AjustesDeRedes['imagen']) =>
    onAjustes((a) => ({ ...a, imagen: fn(a.imagen) }));
  const setVideo = (fn: (v: AjustesDeRedes['video']) => AjustesDeRedes['video']) =>
    onAjustes((a) => ({ ...a, video: fn(a.video) }));
  const setFormato = (slug: string) =>
    onAjustes((a) => (a.medio === 'video' ? { ...a, video: { ...a.video, formatSlug: slug } } : { ...a, imagen: { ...a.imagen, formatSlug: slug } }));

  const formato = formatoActivo(ajustes);
  const aj = ajustes.video.ajustes;
  const duracionMax = videoDuracionMax(aj.quality);
  const modeloCredito = aj.quality === 'profesional' ? 'pro' : 'standard';

  /** La duración (video) o la cantidad (imagen): van a la DERECHA, al lado del Generar. */
  const ajusteDelMedio = esVideo ? (
    <>

        <>
          <button ref={refTiempo} type="button" onClick={() => alternar('tiempo')} aria-expanded={panel === 'tiempo'} className={`${PASTILLA} tabular-nums ${panel === 'tiempo' ? PASTILLA_ABIERTA : ''}`}>
            {aj.durationSeconds} seg
          </button>
          <PanelSobrePastilla botonRef={refTiempo} abierto={panel === 'tiempo'} onCerrar={cerrar} ancho={280}>
            <div className="px-3 py-2.5">
              <div className="mb-2.5 flex items-center justify-between gap-3">
                <span className="text-[14px] text-[#02062BE6]">Duración</span>
                <span className="text-[13.5px] font-semibold tabular-nums text-brand-primary">{aj.durationSeconds} seg</span>
              </div>
              {/* El slider de barras con estela (ADR-2026-10-03-wake-slider). Cantidad de
                  barras FIJA: una por segundo dejaba 11 barras gordas en Básica (5–15 s) y 26
                  finitas en Profesional; con 28 se ve igual en las dos. */}
              <WakeSlider
                value={aj.durationSeconds}
                min={VIDEO_DURATION_MIN}
                max={duracionMax}
                step={1}
                bars={28}
                height={40}
                restHeight={12}
                gap={3}
                onChange={(n) => setVideo((v) => ({ ...v, ajustes: { ...v.ajustes, durationSeconds: n } }))}
                ariaLabel="Duración del video en segundos"
                formatValue={(n) => `${n} segundos`}
              />
              <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums text-[#3C3C43]/45">
                <span>{VIDEO_DURATION_MIN} seg</span>
                <span>{duracionMax} seg</span>
              </div>
            </div>
          </PanelSobrePastilla>
        </>
    </>
  ) : (
        /* Cantidad: el contador vive en la pastilla, no en un panel — son dos toques. */
        <span className={`${PASTILLA} gap-1 pl-1.5 pr-1.5`} aria-label="Cantidad de imágenes">
          <button
            type="button"
            aria-label="Menos"
            disabled={ajustes.imagen.cantidad <= 1}
            onClick={() => setImagen((im) => ({ ...im, cantidad: Math.max(1, im.cantidad - 1) }))}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <span className="w-5 text-center text-[13.5px] font-semibold tabular-nums text-[#02062BE6]">{ajustes.imagen.cantidad}</span>
          <button
            type="button"
            aria-label="Más"
            disabled={ajustes.imagen.cantidad >= MAX_CANTIDAD}
            onClick={() => setImagen((im) => ({ ...im, cantidad: Math.min(MAX_CANTIDAD, im.cantidad + 1) }))}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </span>
  );

  const pastillas = (
    <>

      {/* Una sola pastilla de Configuración por medio: Formato + Calidad en imagen; Formato +
          Calidad + Resolución en video (el tiempo tiene la suya, al lado). El rótulo resume
          dice "Configuración"; lo elegido se ve al abrirla. */}
      <button ref={refConfig} type="button" onClick={() => alternar('configuracion')} aria-expanded={panel === 'configuracion'} className={`${PASTILLA} ${panel === 'configuracion' ? PASTILLA_ABIERTA : ''}`}>
        <Settings2 className="h-4 w-4" strokeWidth={2} />
        Configuración
      </button>
      <PanelSobrePastilla botonRef={refConfig} abierto={panel === 'configuracion'} onCerrar={cerrar} ancho={320}>
        <div className="px-3 pb-1 pt-2.5">
          <span className="mb-2 block text-[14px] text-[#02062BE6]">Formato</span>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Formato">
            {FORMAT_OPTIONS_V2.map((f) => {
              const activa = f.id === formato;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="radio"
                  aria-checked={activa}
                  onClick={() => setFormato(f.id)}
                  className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors ${activa ? 'bg-[#1644EB0F] text-brand-primary ring-1 ring-brand-primary' : 'bg-neutral-50 text-[#02062BE6] ring-1 ring-transparent hover:bg-neutral-100'}`}
                >
                  {rotuloDeFormato(f.id)}
                </button>
              );
            })}
          </div>
        </div>
        {esVideo ? (
          <>
            <div className="px-3 pb-1 pt-3">
              <span className="mb-2 block text-[14px] text-[#02062BE6]">Calidad</span>
              <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Calidad del video">
                {([
                  { id: 'profesional', label: 'Profesional', sub: 'hasta 30s' },
                  { id: 'basica', label: 'Básica', sub: 'hasta 15s' },
                ] as { id: VideoQuality; label: string; sub: string }[]).map((opt) => {
                  const activa = opt.id === aj.quality;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      onClick={() => setVideo((v) => ({ ...v, ajustes: conCalidad(v.ajustes, opt.id) }))}
                      className={`rounded-xl px-2 py-2 text-center transition-colors ${activa ? 'bg-[#1644EB0F] ring-1 ring-brand-primary' : 'bg-neutral-50 ring-1 ring-transparent hover:bg-neutral-100'}`}
                    >
                      <span className={`block text-[13.5px] font-medium ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{opt.label}</span>
                      <span className="mt-0.5 block text-[11px] text-[#3C3C43]/55">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-3 pb-2.5 pt-3">
              <span className="mb-2 block text-[14px] text-[#02062BE6]">Resolución</span>
              <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Resolución del video">
                {RESOLUCIONES_VIDEO.map((res) => {
                  const activa = res === aj.resolution;
                  return (
                    <button
                      key={res}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      title={esBorrador(aj.quality, res) ? AYUDA_BORRADOR : undefined}
                      onClick={() => setVideo((v) => ({ ...v, ajustes: { ...v.ajustes, resolution: res as VideoResolution } }))}
                      className={`rounded-xl px-1.5 py-2 text-center transition-colors ${activa ? 'bg-[#1644EB0F] ring-1 ring-brand-primary' : 'bg-neutral-50 ring-1 ring-transparent hover:bg-neutral-100'}`}
                    >
                      <span className={`block text-[13px] font-medium tabular-nums ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{res}</span>
                      <span className="mt-0.5 block text-[10.5px] tabular-nums text-[#3C3C43]/55">
                        {videoGeneralRatePerSec(modeloCredito, res as VideoResolution)} cr/s
                      </span>
                      {esBorrador(aj.quality, res) && <EtiquetaBorrador compacta className="mt-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="px-3 pb-2.5 pt-3">
            <span className="mb-1 block text-[14px] text-[#02062BE6]">Calidad</span>
            {OPCIONES_RESOLUCION.map((o, i) => (
              <Opcion
                key={o.id}
                activa={o.id === ajustes.imagen.calidad}
                onClick={() => setImagen((im) => ({ ...im, calidad: o.id as ResolucionImagen }))}
                detalle={`${o.detalle} ${i + 1} ${i === 0 ? 'crédito' : 'créditos'} por imagen.`}
              >
                {o.label}
              </Opcion>
            ))}
          </div>
        )}
      </PanelSobrePastilla>

      {/* La Biblioteca de prompts reemplaza a "Recientes": el historial sigue adentro,
          junto con favoritos y los catálogos de fragmentos (ADR-2026-10-02-biblioteca-de-prompts). */}
      <button type="button" onClick={() => setBiblioteca(true)} aria-haspopup="dialog" className={PASTILLA}>
        <BookOpen className="h-4 w-4" strokeWidth={2} />
        Prompts
      </button>

      {onAyudaDePrompt && (
        <button type="button" onClick={onAyudaDePrompt} className={PASTILLA}>
          <Sparkles className="h-4 w-4" strokeWidth={2} />
          Ayuda de prompt
        </button>
      )}

      {/* ── SÓLO DEV: forzar el motor de imagen ──────────────────────────────────────
          Pastilla con borde punteado ámbar y la marca DEV, a propósito distinta de las
          demás: no es parte del producto, es la tuerca para probar un motor. Sólo en
          Imagen (el video no tiene motor que forzar: lo elige la calidad). */}
      {onProveedorForzado && !esVideo && (
        <>
          <button
            ref={refModelo}
            type="button"
            onClick={() => alternar('modelo')}
            aria-expanded={panel === 'modelo'}
            title="Sólo cuentas dev: fuerza el motor de imagen"
            className={`inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed px-3 text-[13px] font-medium transform transition-transform duration-300 hover:-translate-y-0.5 ${
              proveedorForzado ? 'border-[#B45309] bg-[#FEF3C7] text-[#92400E]' : 'border-[#D97706]/60 bg-[#FFFBEB] text-[#92400E]/80'
            }`}
          >
            <span className="rounded bg-[#B45309] px-1 py-px text-[9.5px] font-bold tracking-wide text-white">DEV</span>
            <FlaskConical className="h-3.5 w-3.5" strokeWidth={2.25} />
            {proveedorForzado ? etiquetaProveedorDeImagen(proveedorForzado) : 'Modelo: Atlas elige'}
          </button>
          <PanelSobrePastilla botonRef={refModelo} abierto={panel === 'modelo'} onCerrar={cerrar} ancho={340}>
            <div className="px-3 pb-2.5 pt-3">
              <span className="mb-1 block text-[14px] text-[#02062BE6]">Forzar el motor de imagen</span>
              <span className="mb-2 block text-[12px] text-[#3C3C43]/55">
                Sólo cuentas dev. Elegido corre SOLO, sin fallback, y pisa lo que elegiría el escritor.
              </span>
              <Opcion
                activa={!proveedorForzado}
                onClick={() => {
                  onProveedorForzado(null);
                  cerrar();
                }}
                detalle="El escritor declara la tarea y el backend elige la cadena."
              >
                Atlas elige
              </Opcion>
              {PROVEEDORES_DE_IMAGEN.map((p) => (
                <Opcion
                  key={p.id}
                  activa={proveedorForzado === p.id}
                  onClick={() => {
                    onProveedorForzado(p.id);
                    cerrar();
                  }}
                  detalle={p.detalle}
                >
                  {p.label}
                </Opcion>
              ))}
            </div>
          </PanelSobrePastilla>
        </>
      )}

      {/* Mejorar prompt: el escritor necesita algo de qué partir (texto o una referencia). */}
      {onMejorarPrompt && (
        <button
          type="button"
          onClick={onMejorarPrompt}
          disabled={mejorando || generando || (!prompt.trim() && adjuntos.length === 0)}
          aria-busy={mejorando || undefined}
          // Mientras mejora NO se apaga con `disabled:opacity-50`: el botón es la barra de
          // carga y tiene que leerse entero.
          className={`${PASTILLA} relative overflow-hidden ${mejorando ? '' : 'disabled:opacity-50'} disabled:hover:translate-y-0`}
        >
          {/* El fondo del botón como barra de carga: un tono un poco más oscuro que avanza
              lo que suele tardar la mejora (la media medida, por medio). Va hasta el 96 % en
              el DOBLE de esa media con una curva que frena, así a la media ya llenó casi
              todo y si tarda más sigue avanzando despacio en vez de quedarse clavada; al
              volver la respuesta completa de golpe y se va con el tilde. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 bg-[#02062B]/[0.09]"
            style={{
              width: mejorando ? (arcoArrancado ? '96%' : '0%') : resultadoDeMejora === 'listo' ? '100%' : '0%',
              opacity: mejorando || resultadoDeMejora === 'listo' ? 1 : 0,
              transition: mejorando
                ? arcoArrancado
                  ? `width ${DURACION_MEDIA_DE_MEJORA_MS[ajustes.medio] * 2}ms cubic-bezier(0.15, 0.6, 0.3, 1)`
                  : 'none'
                : resultadoDeMejora === 'listo'
                  ? 'width 220ms ease-out'
                  : 'opacity 300ms ease, width 0ms linear 300ms',
            }}
          />
          <span className="relative inline-flex items-center gap-1.5">
            {marcaDeMejora ? (
              <StatusMark status={marcaDeMejora} size={16} strokeWidth={2.25} doneColor="#16A34A" errorColor="#DC2626" />
            ) : (
              <Wand2 className="h-4 w-4" strokeWidth={2} />
            )}
            {mejorando ? 'Mejorando…' : resultadoDeMejora === 'listo' ? 'Mejorado' : resultadoDeMejora === 'fallo' ? 'No se pudo' : 'Mejorar prompt'}
          </span>
        </button>
      )}
    </>
  );

  return (
    <div className={className}>
      <BibliotecaDePrompts
        abierto={biblioteca}
        onCerrar={() => setBiblioteca(false)}
        prompt={prompt}
        onPromptChange={onPromptChange}
        tipoDeHistorial={esVideo ? 'video' : 'prompt_libre'}
      />
      {/* Las tarjetas de referencia, arriba de la caja y alineadas con ella (no con el
          interruptor). */}
      <div className="flex items-end gap-3">
      <ToggleDeMedio value={ajustes.medio} onChange={(medio) => onAjustes((a) => ({ ...a, medio }))} />
      <div
        style={{ minHeight: ALTO_CAJA }}
        // Sin `overflow-hidden`: las fotos de las carpetitas de arriba salen por encima
        // del botón (y del marco) al abrirse, y el recorte las cortaba.
        className="flex min-w-0 flex-1 rounded-[22px] bg-white shadow-[0_28px_70px_-24px_rgba(2,6,43,0.32),0_2px_6px_rgba(2,6,43,0.04)] ring-1 ring-black/[0.04] transition-shadow focus-within:ring-[#1644EB40]"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Las tarjetas de referencia, ADENTRO del marco y arriba del prompt: las tres
              se reparten el ancho en partes iguales. */}
          <div className="flex gap-2 px-3 pt-3">
            <TarjetaDeReferencia
              rotulo="Añadir producto"
              Icono={Package}
              adjuntos={productos}
              carpeta={{ imagenes: userProducts.map((p) => p.imageUrl).filter(Boolean), tono: 'azul' }}
              onAbrir={() => onAbrirFuentes('productos')}
              onQuitar={() => productos.forEach((a) => onQuitarAdjunto(a.id))}
            />
            <TarjetaDeReferencia
              rotulo={`Añadir ${palabraDeModeloActual().singular}`}
              Icono={User}
              adjuntos={modelos}
              carpeta={{ imagenes: userModels.map((m) => m.thumbnailUrl ?? '').filter(Boolean), tono: 'violeta' }}
              onAbrir={() => onAbrirFuentes('modelos')}
              onQuitar={() => modelos.forEach((a) => onQuitarAdjunto(a.id))}
            />
            <TarjetaDeReferencia
              rotulo="Añadir referencia"
              esDestino
              Icono={Upload}
              adjuntos={subidas}
              // Pegar o soltar una imagen la sube acá: mientras viaja, la tarjeta lo dice.
              estado={subiendo ? 'Subiendo…' : undefined}
              carpeta={{
                imagenes: [],
                tono: 'gris',
                relleno: subiendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} /> : <Upload className="h-3.5 w-3.5" strokeWidth={2.25} />,
              }}
              onAbrir={() => onAbrirFuentes('subidas')}
              onQuitar={() => subidas.forEach((a) => onQuitarAdjunto(a.id))}
            />
          </div>
          <div className="min-h-0 flex-1">
          <PromptBoxLibre
            marco="ninguno"
            micAntesDeDerecha
            prompt={prompt}
            onPromptChange={onPromptChange}
            onSubmit={onGenerar}
            menusDeTexto
            mencionables={adjuntos}
            reboteAlAgregar
            pulsoDeTexto={pulsoDeTexto}
            adjuntos={otros}
            onQuitarAdjunto={onQuitarAdjunto}
            conTipo
            disabled={generando}
            placeholder={placeholderDeRedes(ajustes)}
            izquierda={pastillas}
            derecha={
              <>
              {ajusteDelMedio}
              <button
                type="button"
                onClick={onGenerar}
                disabled={!!falta || generando}
                title={falta}
                // Prender y apagar es un FUNDIDO: el fondo de abajo es el azul claro de apagado
                // y el degradé va en una capa encima que aparece o se va con la opacidad (un
                // `background-image` no se puede transicionar). La sombra acompaña.
                className="group relative inline-flex h-10 shrink-0 items-center gap-2 overflow-hidden rounded-full bg-[#C9D2F3] py-2 px-5 text-[14px] font-semibold text-white shadow-[0_10px_24px_-10px_rgba(22,68,235,0.65)] transform transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:shadow-[0_10px_24px_-10px_rgba(22,68,235,0)] disabled:hover:translate-y-0"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-[linear-gradient(181.87deg,#2563F5_8.76%,#0436AE_90.56%)] opacity-100 transition-opacity duration-300 ease-out group-disabled:opacity-0"
                />
                <span className="relative">Generar</span>
                {/* El costo con el MISMO ícono de créditos de toda la app, en su pastilla
                    blanca, como el botón grande del Creador en el teléfono. */}
                {/* Apagado el botón, la pastilla del costo se apaga con él (ícono incluido). */}
                <span className="relative inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[12.5px] font-semibold tabular-nums text-brand-black transition-opacity duration-300 ease-out group-disabled:opacity-50">
                  <img src="/images/token_icon.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" />
                  <NumeroRodante valor={creditos} />
                </span>
              </button>
              </>
            }
            // Sólo con un tipo elegido: `PromptBoxLibre` dibuja la fila de chips si hay
            // `anclado`, aunque el componente no pinte nada (en General), y esa fila vacía
            // hacía más alta la caja en Video que en Imagen.
            anclado={
              esVideo && ajustes.video.tipoVideo !== 'general' ? (
                <TipoDeVideoAnclado valor={ajustes.video.tipoVideo} onChange={(slug) => setVideo((v) => ({ ...v, tipoVideo: slug }))} />
              ) : undefined
            }
          />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CajaDeRedes;
